import React, { useEffect, useMemo, useState } from 'react';
import { withTranslation } from 'react-i18next';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import GetApp from '@mui/icons-material/GetApp';
import Search from '@mui/icons-material/Search';
import ILocalAGGrid, { getDefaultColDef } from '../../grid/iLocalAGGrid';
import {
  displaySafeText,
  fetchFeaturePurchases,
} from '../../../utils/productLicensing';
import './feature-purchases.css';

const ALL_FEATURES = '__all__';
const STATUS_PURCHASED = 'purchased';
const STATUS_DISABLED = 'disabled';
const STATUS_ALL = 'all';

const formatWhen = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch (_err) {
    return String(value);
  }
};

const csvEscape = (value) => `"${String(value == null ? '' : value).replace(/"/g, '""')}"`;

const StatusCell = (params) => {
  const purchased = params.data && params.data.status === STATUS_PURCHASED;
  return (
    <span
      className={`feature-card-status ${purchased ? 'is-enabled' : 'is-locked'}`}
    >
      {params.value}
    </span>
  );
};

const FeaturePurchasesReport = ({ t }) => {
  const isIlocalUser = localStorage.getItem('isIlocalUser') === 'true';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [features, setFeatures] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [featureFilter, setFeatureFilter] = useState(ALL_FEATURES);
  const [statusFilter, setStatusFilter] = useState(STATUS_PURCHASED);

  const purchasedLabel = t('FeaturePurchases.Purchased', {
    defaultValue: 'Purchased',
  });
  const disabledLabel = t('FeaturePurchases.Disabled', {
    defaultValue: 'Disabled',
  });

  const featureOptions = useMemo(
    () => [
      {
        id: ALL_FEATURES,
        name: t('FeaturePurchases.AllFeatures', { defaultValue: 'All features' }),
      },
      ...(features || []).map((feature) => ({
        id: feature.id,
        name: displaySafeText(feature.name, ''),
      })).filter((feature) => feature.name),
    ],
    [features, t]
  );

  const statusOptions = useMemo(
    () => [
      {
        id: STATUS_PURCHASED,
        name: purchasedLabel,
      },
      {
        id: STATUS_DISABLED,
        name: disabledLabel,
      },
      {
        id: STATUS_ALL,
        name: t('FeaturePurchases.AllStatuses', { defaultValue: 'All statuses' }),
      },
    ],
    [disabledLabel, purchasedLabel, t]
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await fetchFeaturePurchases({ status: 'all' });
        if (!cancelled) {
          setRows((data && data.rows) || []);
          setFeatures((data && data.features) || []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            (err && err.message) ||
              t('FeaturePurchases.LoadError', {
                defaultValue: 'Unable to load feature purchases right now.',
              })
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    if (isIlocalUser) {
      load();
    } else {
      setLoading(false);
    }
    return () => {
      cancelled = true;
    };
  }, [isIlocalUser, t]);

  const filteredRows = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    return (rows || []).filter((row) => {
      if (statusFilter !== STATUS_ALL && row.status !== statusFilter) {
        return false;
      }
      if (featureFilter !== ALL_FEATURES && row.feature_key !== featureFilter) {
        return false;
      }
      if (!query) {
        return true;
      }
      const account = String(row.account_name || '').toLowerCase();
      const feature = String(row.feature_title || '').toLowerCase();
      const actor = String(row.enabled_by || '').toLowerCase();
      return (
        account.includes(query) ||
        feature.includes(query) ||
        actor.includes(query)
      );
    });
  }, [rows, searchText, featureFilter, statusFilter]);

  const purchasedCount = filteredRows.filter(
    (row) => row.status === STATUS_PURCHASED
  ).length;
  const accountCount = new Set(
    filteredRows
      .filter((row) => row.status === STATUS_PURCHASED)
      .map((row) => row.account_name)
  ).size;

  const selectedFeature =
    featureOptions.find((item) => item.id === featureFilter) || featureOptions[0];
  const selectedStatus =
    statusOptions.find((item) => item.id === statusFilter) || statusOptions[0];

  const columnDefs = useMemo(
    () => [
      {
        field: 'account_name',
        colId: 'account_name',
        headerName: t('FeaturePurchases.Account', { defaultValue: 'Account' }),
        valueGetter: (params) =>
          displaySafeText(params.data && params.data.account_name, '—'),
        minWidth: 200,
      },
      {
        field: 'feature_title',
        colId: 'feature_title',
        headerName: t('FeaturePurchases.Feature', { defaultValue: 'Feature' }),
        valueGetter: (params) =>
          displaySafeText(params.data && params.data.feature_title, '—'),
        minWidth: 180,
      },
      {
        field: 'status',
        colId: 'status',
        headerName: t('FeaturePurchases.Status', { defaultValue: 'Status' }),
        valueGetter: (params) =>
          params.data && params.data.status === STATUS_PURCHASED
            ? purchasedLabel
            : disabledLabel,
        cellRenderer: StatusCell,
        minWidth: 140,
      },
      {
        field: 'purchased_on',
        colId: 'purchased_on',
        headerName: t('FeaturePurchases.PurchasedOn', {
          defaultValue: 'Purchased on',
        }),
        valueFormatter: (params) => formatWhen(params.value),
        minWidth: 200,
      },
      {
        field: 'enabled_by',
        colId: 'enabled_by',
        headerName: t('FeaturePurchases.EnabledBy', {
          defaultValue: 'Enabled by',
        }),
        valueGetter: (params) =>
          displaySafeText(params.data && params.data.enabled_by, '—'),
        minWidth: 200,
      },
    ],
    [disabledLabel, purchasedLabel, t]
  );

  const handleExport = () => {
    const header = [
      t('FeaturePurchases.Account', { defaultValue: 'Account' }),
      t('FeaturePurchases.Feature', { defaultValue: 'Feature' }),
      t('FeaturePurchases.Status', { defaultValue: 'Status' }),
      t('FeaturePurchases.PurchasedOn', { defaultValue: 'Purchased on' }),
      t('FeaturePurchases.EnabledBy', { defaultValue: 'Enabled by' }),
    ];
    const lines = [
      header.map(csvEscape).join(','),
      ...filteredRows.map((row) =>
        [
          displaySafeText(row.account_name, '—'),
          displaySafeText(row.feature_title, '—'),
          row.status === STATUS_PURCHASED ? purchasedLabel : disabledLabel,
          formatWhen(row.purchased_on),
          displaySafeText(row.enabled_by, '—'),
        ]
          .map(csvEscape)
          .join(',')
      ),
    ];
    const blob = new Blob([lines.join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'feature-purchases.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (!isIlocalUser) {
    return (
      <div className="feature-purchases">
        <p className="licensing-empty">
          {t('LicensingAdmin.Forbidden', {
            defaultValue: 'This page is only available to iLocal members.',
          })}
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="feature-purchases">
        <CircularProgress size={28} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="feature-purchases">
        <p className="licensing-empty">{error}</p>
      </div>
    );
  }

  return (
    <div className="feature-purchases">
      <div className="feature-purchases-hero">
        <p className="features-hero-eyebrow">
          {t('FeaturePurchases.Eyebrow', { defaultValue: 'Licensing' })}
        </p>
        <p className="features-intro">
          {t('FeaturePurchases.Intro', {
            defaultValue:
              'See which pharmacy accounts have purchased each licensed feature. Enablement is recorded after the offline sale. This report does not collect payment.',
          })}
        </p>
        <div className="features-hero-meta">
          <span>
            {t('FeaturePurchases.PurchasedCount', {
              defaultValue: '{{count}} purchased',
              count: purchasedCount,
            })}
          </span>
          <span>
            {t('FeaturePurchases.AccountCount', {
              defaultValue: '{{count}} accounts',
              count: accountCount,
            })}
          </span>
        </div>
      </div>

      <div className="feature-purchases-toolbar">
        <TextField
          className="feature-purchases-search"
          variant="outlined"
          size="small"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          placeholder={t('FeaturePurchases.SearchPlaceholder', {
            defaultValue: 'Search account, feature, or email...',
          })}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" sx={{ color: '#64748b' }} />
              </InputAdornment>
            ),
          }}
        />
        <Autocomplete
          className="feature-purchases-filter"
          options={featureOptions}
          value={selectedFeature}
          disableClearable
          getOptionLabel={(option) => (option && option.name) || ''}
          isOptionEqualToValue={(option, value) =>
            !!(option && value && option.id === value.id)
          }
          onChange={(_event, value) =>
            setFeatureFilter((value && value.id) || ALL_FEATURES)
          }
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label={t('FeaturePurchases.Feature', { defaultValue: 'Feature' })}
            />
          )}
        />
        <Autocomplete
          className="feature-purchases-filter"
          options={statusOptions}
          value={selectedStatus}
          disableClearable
          getOptionLabel={(option) => (option && option.name) || ''}
          isOptionEqualToValue={(option, value) =>
            !!(option && value && option.id === value.id)
          }
          onChange={(_event, value) =>
            setStatusFilter((value && value.id) || STATUS_PURCHASED)
          }
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label={t('FeaturePurchases.Status', { defaultValue: 'Status' })}
            />
          )}
        />
        <Button
          className="feature-purchases-export"
          variant="contained"
          startIcon={<GetApp />}
          disabled={filteredRows.length === 0}
          onClick={handleExport}
          sx={{
            textTransform: 'none',
            backgroundColor: '#00a7e1',
            borderRadius: '10px',
            fontWeight: 600,
            boxShadow: '0 8px 18px rgba(0, 167, 225, 0.28)',
            '&:hover': { backgroundColor: '#0096cc' },
          }}
        >
          {t('FeaturePurchases.Export', { defaultValue: 'Export CSV' })}
        </Button>
      </div>

      <ILocalAGGrid
        className="ag-theme-alpine feature-purchases-grid"
        columnDefs={columnDefs}
        defaultColDef={getDefaultColDef()}
        rowModelType="clientSide"
        rowData={filteredRows}
        pagination={true}
        paginationPageSize={25}
        paginationPageSizeSelector={[10, 20, 25]}
        rowHeight={48}
        overlayNoRowsMessage={t('FeaturePurchases.Empty', {
          defaultValue: 'No feature purchases match this search.',
        })}
        getRowId={(params) => {
          const row = params.data || {};
          return [
            row.account_name,
            row.feature_key,
            row.purchased_on,
            row.enabled_by,
            row.status,
          ].join('|');
        }}
      />
    </div>
  );
};

export default withTranslation()(FeaturePurchasesReport);



// WEBPACK FOOTER //
// ./src/components/reports/feature-purchases/index.js